// Central API client. Every backend call is
//   POST `${VITE_API_BASE_URL}/<endpoint>` with body { action, parameters }.
// Success: { data } (unwrapped and returned). Failure: non-2xx + { error, code? }.
import { clearSession, getIdToken } from "./auth";

export type ApiEndpoint =
  | "content"
  | "people"
  | "issues"
  | "admin"
  | "media"
  | "store"
  | "health";

export class ApiError extends Error {
  status: number;
  code?: string;
  /** Per-field messages from a `code: "VALIDATION"` 400, keyed by field path. */
  fields?: Record<string, string>;

  constructor(message: string, status: number, code?: string, fields?: Record<string, string>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

export async function fetchApi<T = unknown>(
  endpoint: ApiEndpoint,
  action: string,
  parameters: Record<string, unknown> = {},
  opts: { auth?: boolean } = {},
): Promise<T> {
  const { auth = true } = opts;
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth) {
    const token = await getIdToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${base}/${endpoint}`, {
    method: "POST",
    headers,
    body: JSON.stringify({ action, parameters }),
  });

  if (res.status === 401) {
    clearSession();
    window.location.assign("/auth");
    throw new ApiError("Your session has expired. Please sign in again.", 401);
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // non-JSON body — handled below
  }

  if (!res.ok) {
    const err = (body ?? {}) as { error?: string; code?: string; fields?: Record<string, string> };
    throw new ApiError(
      err.error || `Request failed (${res.status})`,
      res.status,
      err.code,
      err.fields,
    );
  }

  return (body as { data: T }).data;
}
